// Canonical reservation-tracker rules: when a tracked place's booking
// window opens, and whether the entry still needs booking.
//
// Consumers (this file is the ONE source of these rules):
//   • Worker   — src/lib/agenda-core.js (MCP get_agenda),
//                src/lib/mcp-day-view.js (get_day / get_week's to-book rows)
//                and src/lib/home-plan-core.js (Save to Plans openings)
//   • Web      — @core/reservation-core: routes/Reservations.tsx,
//                GlobalReservations.tsx, Overview.tsx, MapMode.tsx,
//                components/ReservationTeaser.tsx, lib/all-reservations.ts
//   • Mobile   — mobile/lib/reservation-core.js, a BYTE-IDENTICAL twin
//                maintained ONLY by `bash scripts/sync_core.sh
//                reservation-core` (make sync-reservation-core). Never
//                hand-edit the mobile copy. It imports its sibling twins
//                ./plan-core.js and ./day-plan-core.js.
//   • WaageWu  — reads get_agenda's answer; it no longer mirrors these.
//
// Semantics are pinned by scripts/smoke_reservation_core.mjs (make
// smoke-reservation-core); the sync script runs it.
//
// Before this module the rules lived in four places: web and mobile
// reservation-math.ts, mcp-day-view.js's closed-status list, and WaageWu's
// agenda-core.ts. They disagreed: only the Worker knew the 'canceled' and
// 'confirmed' spellings, and mobile fell back to the Japan trip's hardcoded
// start (Dec 17) as the target date of ANY trip's entry that had none.
//
// Rules:
//   0. The window opens on the entry's explicit `opensOn` (staggered luxury
//      releases, anything no rule below computes). Else, when the entry
//      has a target date:
//      a. A monthly release named in `opensAt` ("1st of month, 12:00 JST
//         (two months ahead)", "25th of each month … following month",
//         "Last day of each month for two months ahead"): day K of the
//         month M months before the target's month. MAZ, Dec 27, K = 1,
//         M = 2 → Oct 1. M comes from the text ("two months ahead",
//         "following month" = 1, "6 months incl. current" = 5), else from
//         opensDaysAhead / 30 rounded. K past the month's end clamps to it.
//      b. "N months ahead / prior" with no day of month: the target's day
//         N calendar months earlier.
//      c. Otherwise target date minus opensDaysAhead.
//      The entry's opensAt is read first and the catalog's
//      (opts.catalog.opensAt) fills what it leaves out.
//   1. The entry's opensDaysAhead wins; the catalog snapshot
//      (opts.catalog.opensDaysAhead) is the fallback for entries seeded
//      before the catalog had one.
//   2. An entry without a target date and without opensOn has no window:
//      it needs a date (countdown reason 'needs-date', label "Needs a
//      date"). There is no trip-start fallback (removed 2026-09-29, #590:
//      it put MAZ's December window on Oct 18 instead of Oct 1).
//      opts.tripStart is accepted and ignored.
//   3. An opensAt that names a release pattern the rules above don't
//      compute (weekly, quarterly, "release date varies", a weekday) sets
//      ruleUnparsed: the window, if any, is days-ahead math and only an
//      estimate; with no days ahead there is no window and the countdown
//      says "Release rule not computed".
//   4. The open moment: the clock time opensAt names (day-plan-core
//      parseClockTime) in the zone it names (zoneFromText), else in
//      opts.zone (the caller's zone for the venue, e.g. the trip's zone on
//      the target date). reservationOpenMoment returns it with the instant.
//   5. Countdown tones: < 0 past · 0 now · ≤ 7 imminent · > 7 future;
//      'no-data' with a reason when there is no window.
//   6. dismissed / cancelled / canceled → dropped from every list.
//      secured / confirmed, or an active booking (plan-core
//      isActiveBooking) with the same refId → booked (#365, #567). refId
//      is the match key because booking `kind` is remapped on convert
//      (bar → restaurant). A pending booking (status 'pending': requested,
//      the venue hasn't confirmed) is active but does not secure the
//      entry: it stays to book, and classifyReservation marks it
//      `requested` so the row can say so.
//   7. Anything else is to book: 'book' once the window is open (today or
//      earlier), else 'queued'. Legacy planned / opened / missed fall
//      through to the date math; being past the opening day doesn't close
//      the window (some venues open months out and stay open).
//   8. Who an entry is for (bookerOf): the person who books it and gets
//      the reminder. `booker` (an email) on a tracker entry or a trip
//      to-do when set, else the trip's owner (tripOwnerOf: meta.createdBy,
//      else the first member with role owner, as trip-admin.js
//      principalEmailForTrip and list_trips' `owner` read it). Emails
//      compare lowercased. get_agenda shows a window or a to-do only to
//      its person; the iOS app reminds only its person.
//
// Pure; it imports the sibling cores plan-core.js and day-plan-core.js.
// Nothing here reads the clock except when a caller omits opts.today (then
// the device's local date).

import { parseClockTime, zonedInstant, zoneFromText, zoneLabel } from './day-plan-core.js';
import { isActiveBooking, isPendingBooking } from './plan-core.js';

export const DROPPED_RESERVATION_STATUSES = new Set(['dismissed', 'cancelled', 'canceled']);
export const SECURED_RESERVATION_STATUSES = new Set(['secured', 'confirmed']);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function pad2(n) {
  return String(n).padStart(2, '0');
}

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function isoDate(v) {
  return typeof v === 'string' && ISO_DATE_RE.test(v) ? v : null;
}

function addDaysISO(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysBetweenISO(fromISO, toISO) {
  return Math.round((Date.parse(`${toISO}T00:00:00Z`) - Date.parse(`${fromISO}T00:00:00Z`)) / 86_400_000);
}

// The days-ahead figure in force: the entry's, else the catalog's.
function daysAheadOf(r, catalog) {
  if (typeof r.opensDaysAhead === 'number' && Number.isFinite(r.opensDaysAhead)) return r.opensDaysAhead;
  if (catalog && typeof catalog.opensDaysAhead === 'number' && Number.isFinite(catalog.opensDaysAhead)) {
    return catalog.opensDaysAhead;
  }
  return null;
}

const NUMBER_WORDS = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};
const COUNT = '(\\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)';
const MONTHS_INCL_CURRENT_RE = new RegExp(`~?${COUNT} months? incl(?:\\.|uding)? (?:the )?current`);
const MONTHS_AHEAD_RE = new RegExp(`~?${COUNT} months? (?:ahead|in advance|out|prior|before)`);

function countOf(word) {
  const n = /^\d+$/.test(word) ? Number(word) : NUMBER_WORDS[word];
  return Number.isInteger(n) ? n : null;
}

// Text that reads monthly but fits no fixed day-of-month rule: "monthly
// (release date varies)", "1st of month, 10:00 JST (quarterly)".
const NOT_MONTHLY_RE = /\b(quarterly|bimonthly|weekly|varies|vary|lottery|phase|annual|year-end)\b/;
// Words that make opensAt a release pattern rather than a clock time.
const RULE_WORDS_RE =
  /\b(month|months|monthly|week|weekly|quarter|quarterly|bimonthly|annual|year|lottery|varies|phase|prior|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b|\d(st|nd|rd|th)\b/;
const LAST_DAY_RE = /\blast day of (?:each |every |the )?month\b/;
const DAY_OF_MONTH_RES = [
  /\b(?:(\d{1,2})(?:st|nd|rd|th)|first) of (?:each |every |the )?(?:preceding |previous |following |next )?month\b/,
  /\bmonthly on the (\d{1,2})(?:st|nd|rd|th)\b/,
];

/**
 * Read a free-text opensAt into its parts, or null for empty input:
 *   { day, monthsAhead, time, zone, pattern }
 * day          1–31 or 'last': the day of month a monthly release falls on.
 * monthsAhead  calendar months before the target's month, when the text
 *              says ("two months ahead", "following month" = 1,
 *              "6 months incl. current" = 5).
 * time / zone  'HH:MM' and IANA zone, when named.
 * pattern      the text names a release pattern, not only a clock time.
 */
export function parseOpensAt(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const t = text.toLowerCase().replace(/\s+/g, ' ');
  const monthly = !NOT_MONTHLY_RE.test(t);
  let day = null;
  let monthsAhead = null;
  if (monthly) {
    if (LAST_DAY_RE.test(t)) day = 'last';
    else {
      for (const re of DAY_OF_MONTH_RES) {
        const m = re.exec(t);
        if (m) {
          const k = m[1] ? Number(m[1]) : 1;
          if (k >= 1 && k <= 31) day = k;
          break;
        }
      }
    }
    const incl = MONTHS_INCL_CURRENT_RE.exec(t);
    const ahead = MONTHS_AHEAD_RE.exec(t);
    if (incl) {
      const n = countOf(incl[1]);
      if (n != null && n >= 2) monthsAhead = n - 1;
    } else if (ahead) {
      monthsAhead = countOf(ahead[1]);
    } else if (/\b(?:following|next|preceding|previous) month\b/.test(t)) {
      monthsAhead = 1;
    }
  }
  return { day, monthsAhead, time: parseClockTime(text), zone: zoneFromText(text), pattern: RULE_WORDS_RE.test(t) };
}

// The date `months` calendar months before `iso`'s month, on day `day`
// ('last' = the month's last day; null = `iso`'s own day). A day past the
// month's end clamps to it.
function monthsBeforeISO(iso, months, day) {
  const [y, m, d] = iso.split('-').map(Number);
  const idx = y * 12 + (m - 1) - months;
  const year = Math.floor(idx / 12);
  const month1 = (idx % 12) + 1;
  const dim = new Date(Date.UTC(year, month1, 0)).getUTCDate();
  const k = day === 'last' ? dim : Math.min(day == null ? d : day, dim);
  return `${year}-${pad2(month1)}-${pad2(k)}`;
}

function textOf(v) {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * The booking window, rules 0–4:
 *   { opensOn, basis, needsDate, ruleUnparsed, rule, time, zone, zoneLabel, at }
 * opensOn       'YYYY-MM-DD' or null.
 * basis         'explicit' | 'monthly' | 'months-ahead' | 'days-ahead' | null.
 * needsDate     no target date and no opensOn.
 * ruleUnparsed  opensAt names a release pattern the rules don't compute.
 * rule          the opensAt text in force (the entry's, else the catalog's).
 * time, zone, zoneLabel, at  the open moment: 'HH:MM', IANA zone, "JST",
 *               and the instant in ms since epoch; null where unknown.
 * opts: { catalog, zone } — zone is the venue's zone for when opensAt names
 * a time but no zone.
 */
export function reservationWindow(r, opts) {
  const catalog = opts && opts.catalog;
  const ownText = r ? textOf(r.opensAt) : null;
  const catText = catalog ? textOf(catalog.opensAt) : null;
  const own = parseOpensAt(ownText);
  const cat = parseOpensAt(catText);
  const pick = (k) => (own && own[k] != null ? own[k] : cat && cat[k] != null ? cat[k] : null);
  const target = r ? isoDate(r.targetDate) : null;

  let opensOn = r ? isoDate(r.opensOn) : null;
  let basis = opensOn ? 'explicit' : null;
  if (r && !opensOn && target) {
    const day = pick('day');
    const ahead = daysAheadOf(r, catalog);
    let months = pick('monthsAhead');
    if (day != null && months == null && ahead != null) months = Math.round(ahead / 30);
    if (months != null && months >= 1) {
      opensOn = monthsBeforeISO(target, months, day);
      basis = day != null ? 'monthly' : 'months-ahead';
    } else if (ahead != null) {
      opensOn = addDaysISO(target, -ahead);
      basis = 'days-ahead';
    }
  }

  const pattern = !!((own && own.pattern) || (cat && cat.pattern));
  const time = pick('time');
  const fallbackZone = opts && typeof opts.zone === 'string' && opts.zone ? opts.zone : null;
  const zone = time ? pick('zone') || fallbackZone : null;
  const at = opensOn && time && zone ? zonedInstant(opensOn, time, zone) : null;
  return {
    opensOn,
    basis,
    needsDate: !!r && !opensOn && !target,
    ruleUnparsed: pattern && basis !== 'explicit' && basis !== 'monthly' && basis !== 'months-ahead',
    rule: ownText || catText,
    time,
    zone,
    zoneLabel: zone ? zoneLabel(zone, opensOn) : null,
    at: at == null ? null : at,
  };
}

/** The day the booking window opens ('YYYY-MM-DD'), or null. Rules 0–2. */
export function reservationOpensOn(r, opts) {
  return reservationWindow(r, opts).opensOn;
}

/**
 * When the window opens, rule 4: { date, time, zone, zoneLabel, at }, or
 * null with no window. time / zone / zoneLabel / at are null when opensAt
 * names no time (or no zone and opts.zone is unset).
 */
export function reservationOpenMoment(r, opts) {
  const w = reservationWindow(r, opts);
  if (!w.opensOn) return null;
  return { date: w.opensOn, time: w.time, zone: w.zone, zoneLabel: w.zoneLabel, at: w.at };
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dateText(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${DOW[d.getUTCDay()]} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * An open moment as text: { venue, viewer }.
 *   venue   "Thu Oct 1, 12:00 JST"; "Thu Oct 1" when no time is known.
 *   viewer  the same instant on viewerZone's clock, "Wed Sep 30, 8:00 PM";
 *           null without an instant or a viewer zone, or when the viewer's
 *           zone is the venue's.
 */
export function openMomentText(moment, viewerZone) {
  if (!moment || !isoDate(moment.date)) return { venue: '', viewer: null };
  const venue = moment.time
    ? `${dateText(moment.date)}, ${moment.time}${moment.zoneLabel ? ` ${moment.zoneLabel}` : ''}`
    : dateText(moment.date);
  if (moment.at == null || typeof viewerZone !== 'string' || !viewerZone || viewerZone === moment.zone) {
    return { venue, viewer: null };
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: viewerZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(new Date(moment.at));
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
    const viewer = `${get('weekday')} ${get('month')} ${get('day')}, ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
    return { venue, viewer };
  } catch {
    return { venue, viewer: null };
  }
}

const NO_WINDOW_LABEL = {
  'needs-date': 'Needs a date',
  'rule-unparsed': 'Release rule not computed',
  'no-rule': 'No opens-window pinned',
};

/**
 * Where the window stands relative to today, rule 5: { tone, label,
 * daysFromToday, opensOnISO, reason, ruleUnparsed }. Labels are the
 * tracker's own ("opens in 3 days", "window open · 2 days ago"). With no
 * window the tone is 'no-data' and reason says why:
 *   'needs-date'     no target date and no opensOn    → "Needs a date"
 *   'rule-unparsed'  opensAt names a rule not computed → "Release rule not computed"
 *   'no-rule'        nothing to compute from          → "No opens-window pinned"
 * With a window, reason is null and ruleUnparsed marks a days-ahead
 * estimate standing in for a rule the core can't read.
 */
export function reservationCountdown(r, opts) {
  const w = reservationWindow(r, opts);
  const opensOnISO = w.opensOn;
  if (!opensOnISO) {
    const reason = w.needsDate ? 'needs-date' : w.ruleUnparsed ? 'rule-unparsed' : 'no-rule';
    return {
      tone: 'no-data',
      label: NO_WINDOW_LABEL[reason],
      daysFromToday: null,
      opensOnISO: null,
      reason,
      ruleUnparsed: w.ruleUnparsed,
    };
  }
  const today = isoDate(opts && opts.today) || localToday();
  const daysFromToday = daysBetweenISO(today, opensOnISO);
  let tone;
  let label;
  if (daysFromToday < 0) {
    const n = -daysFromToday;
    tone = 'opens-past';
    label = `window open · ${n} day${n === 1 ? '' : 's'} ago`;
  } else if (daysFromToday === 0) {
    tone = 'opens-now';
    label = 'opens today';
  } else if (daysFromToday <= 7) {
    tone = 'opens-imminent';
    label = `opens in ${daysFromToday} day${daysFromToday === 1 ? '' : 's'}`;
  } else {
    tone = 'opens-future';
    label = `opens in ${daysFromToday} days`;
  }
  return { tone, label, daysFromToday, opensOnISO, reason: null, ruleUnparsed: w.ruleUnparsed };
}

/** Dismissed or cancelled: the entry leaves every list. */
export function isReservationDropped(r) {
  return !r || DROPPED_RESERVATION_STATUSES.has(r.status);
}

/** A confirmed booking (active, not pending) links the same catalog place. */
export function reservationHasBooking(r, bookings) {
  if (!r || !r.refId || !Array.isArray(bookings)) return false;
  return bookings.some((b) => b && b.refId === r.refId && isActiveBooking(b) && !isPendingBooking(b));
}

/** A pending booking links the same catalog place: asked for, not confirmed. */
export function reservationHasPendingBooking(r, bookings) {
  if (!r || !r.refId || !Array.isArray(bookings)) return false;
  return bookings.some((b) => b && b.refId === r.refId && isActiveBooking(b) && isPendingBooking(b));
}

/** Booked already: a secured status or a matching active booking. Rule 6. */
export function isReservationSecured(r, bookings) {
  return !!r && (SECURED_RESERVATION_STATUSES.has(r.status) || reservationHasBooking(r, bookings));
}

/** Still to book: neither dropped nor secured. */
export function reservationNeedsBooking(r, bookings) {
  return !isReservationDropped(r) && !isReservationSecured(r, bookings);
}

function lowerEmail(v) {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return s || null;
}

/**
 * The trip's owner, lowercased: meta.createdBy, else the first member
 * with role owner. Null when neither is there. Rule 8.
 */
export function tripOwnerOf(meta) {
  const created = lowerEmail(meta && meta.createdBy);
  if (created) return created;
  const members = meta && Array.isArray(meta.members) ? meta.members : [];
  const owner = members.find((m) => m && m.role === 'owner' && lowerEmail(m.email));
  return owner ? lowerEmail(owner.email) : null;
}

/**
 * Who a tracker entry or a trip to-do is for, lowercased: its `booker`
 * when set, else the trip's owner (tripOwnerOf). Null when neither is
 * known. Rule 8.
 */
export function bookerOf(entry, meta) {
  return lowerEmail(entry && entry.booker) || tripOwnerOf(meta);
}

/** Is the entry for this person? Case-insensitive; false when either is unknown. */
export function isBookerOf(entry, meta, email) {
  const who = bookerOf(entry, meta);
  const me = lowerEmail(email);
  return !!who && !!me && who === me;
}

/**
 * The tracker row for an entry, or null when it is dropped:
 *   { reservation, state: 'conf' | 'book' | 'queued', when, countdown, requested }
 * `when` is the row's short text ('confirmed', 'open now', else the
 * countdown label); `requested` is true on a row still to book that has a
 * pending booking (reservationHasPendingBooking). opts: { today, catalog, zone, bookings }.
 */
export function classifyReservation(r, opts) {
  if (isReservationDropped(r)) return null;
  const bookings = opts && opts.bookings;
  const countdown = reservationCountdown(r, opts);
  if (isReservationSecured(r, bookings)) {
    return { reservation: r, state: 'conf', when: 'confirmed', countdown, requested: false };
  }
  const requested = reservationHasPendingBooking(r, bookings);
  if (countdown.tone === 'opens-now' || countdown.tone === 'opens-past') {
    return { reservation: r, state: 'book', when: 'open now', countdown, requested };
  }
  return { reservation: r, state: 'queued', when: countdown.label, countdown, requested };
}

/**
 * Soonest window first: windows already open, then today, this week,
 * later; entries with no window last. Stable. opts: { today,
 * resolveCatalog(r) → catalog snapshot }.
 */
export function sortByCountdown(list, opts) {
  const resolve = opts && typeof opts.resolveCatalog === 'function' ? opts.resolveCatalog : null;
  const days = new Map();
  const arr = Array.isArray(list) ? list.slice() : [];
  for (const r of arr) {
    days.set(r, reservationCountdown(r, { ...(opts || {}), catalog: resolve ? resolve(r) : null }).daysFromToday);
  }
  return arr.sort((a, b) => {
    const da = days.get(a);
    const db = days.get(b);
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  });
}
